// Папки справочника — те же, что у бухгалтера в 1С.
//
// Каталог у завода за тысячу позиций, и до сих пор он лежал плоским списком:
// экран показывал первую сотню, остальное искали строкой. Менеджер при этом
// всю жизнь работал в 1С, где то же самое разложено по папкам — «ЛДСП»,
// внутри «16 мм», «22 мм». Он знает, где смотреть, а у нас смотреть было
// негде.
//
// Поэтому дерево не своё, а из 1С: та же структура, те же названия.
//
// Пока бухгалтер не указал в 1С верхнюю папку, папок не приходит вовсе. Тогда
// колонка просто не показывается, и экран работает как раньше — одним списком.
import React, { useState, useEffect, useCallback } from 'react';
import { warehouseAPI } from '../api';

// Дерево приходит плоским списком: {id, name, parent, count, count_deep}.
export function useFolders(type) {
  const [folders, setFolders] = useState([]);
  const load = useCallback(() => {
    warehouseAPI.getFolders(type ? { type } : undefined)
      .then(r => setFolders(r.data.folders || []))
      .catch(() => setFolders([]));
  }, [type]);
  useEffect(() => { load(); }, [load]);
  return folders;
}

// Папка и всё, что внутри неё. Открыв «ЛДСП», человек ждёт увидеть всё ЛДСП, а
// не пустоту оттого, что позиции лежат на уровень ниже.
export function subtreeIds(folders, rootId) {
  const ids = new Set([rootId]);
  let grew = true;
  let guard = 0;
  while (grew && guard++ < 20) {
    grew = false;
    folders.forEach(f => {
      if (f.parent != null && ids.has(f.parent) && !ids.has(f.id)) {
        ids.add(f.id); grew = true;
      }
    });
  }
  return ids;
}

// Пока 1С не выгружает папки, дерево строим из категорий — тех же, что уже
// проставлены у позиций. Экран должен быть удобен сегодня, а не после того,
// как бухгалтер что-то настроит у себя. Когда папки поедут, они молча займут
// это место: и то и другое для экрана — просто узлы дерева.
const CAT_NAMES = {
  ldsp: 'ЛДСП', dsp: 'ДСП', paper: 'Бумага / Плёнка', other: 'Прочее',
};

export function categoryFolders(items) {
  const n = {};
  (items || []).forEach(i => {
    const k = i.category || 'other';
    n[k] = (n[k] || 0) + 1;
  });
  return Object.keys(n)
    .sort((a, b) => n[b] - n[a])
    .map(k => ({ id: 'cat:' + k, name: CAT_NAMES[k] || k, parent: null,
                 count: n[k], count_deep: n[k] }));
}

// Дерево из 1С, а если его ещё нет — из категорий.
export function treeOf(folders, items) {
  return (folders && folders.length) ? folders : categoryFolders(items);
}

// Отбор по выбранной папке. null — всё, 'none' — то, что вне дерева.
export function inFolder(item, folderId, folders) {
  if (folderId == null) return true;
  if (typeof folderId === 'string' && folderId.startsWith('cat:')) {
    return ('cat:' + (item.category || 'other')) === folderId;
  }
  if (folderId === 'none') return !item.folder;
  return subtreeIds(folders, folderId).has(item.folder);
}

const Row = ({ depth, open, hasKids, active, name, count, onClick, onToggle }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 8px', paddingLeft: 8 + depth * 14,
      borderRadius: 7, cursor: 'pointer', fontSize: 13,
      background: active ? 'var(--accent-weak)' : 'transparent',
      color: active ? 'var(--text)' : 'var(--muted)',
      fontWeight: active ? 700 : 500,
    }}
  >
    <span
      onClick={(e) => { if (hasKids) { e.stopPropagation(); onToggle(); } }}
      style={{
        width: 14, flexShrink: 0, textAlign: 'center', fontSize: 10,
        color: 'var(--dim)', cursor: hasKids ? 'pointer' : 'default',
      }}
    >
      {hasKids ? (open ? '▾' : '▸') : ''}
    </span>
    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                   textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {name}
    </span>
    {count != null && (
      <span style={{ color: 'var(--dim)', fontSize: 11, flexShrink: 0 }}>{count}</span>
    )}
  </div>
);

/**
 * Колонка с папками.
 *
 * folders   — плоский список из useFolders()
 * value     — выбранная папка: null | id | 'none'
 * onChange  — что выбрали
 * total     — сколько позиций всего (для строки «Все»)
 * noFolder  — сколько позиций вне дерева
 */
export default function CatalogTree({ folders, value, onChange, total,
                                      noFolder = 0, style = {} }) {
  // Верхние папки раскрыты сразу: у завода их две-три, и лишний щелчок,
  // чтобы увидеть «16 мм», ничем не оправдан.
  const [open, setOpen] = useState(() => new Set());
  useEffect(() => {
    setOpen(new Set(folders.filter(f => f.parent == null).map(f => f.id)));
  }, [folders.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!folders.length) return null;

  const kids = {};
  folders.forEach(f => {
    const k = f.parent == null ? 'root' : f.parent;
    (kids[k] = kids[k] || []).push(f);
  });

  const toggle = (id) => setOpen(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const draw = (parentKey, depth) => (kids[parentKey] || []).map(f => {
    const hasKids = !!(kids[f.id] || []).length;
    const isOpen = open.has(f.id);
    return (
      <React.Fragment key={f.id}>
        <Row depth={depth} open={isOpen} hasKids={hasKids}
             active={value === f.id} name={f.name} count={f.count_deep}
             onClick={() => onChange(f.id)} onToggle={() => toggle(f.id)} />
        {isOpen && draw(f.id, depth + 1)}
      </React.Fragment>
    );
  });

  return (
    <div style={{
      width: 210, flexShrink: 0, overflowY: 'auto', paddingRight: 8,
      borderRight: '1px solid var(--border)', ...style,
    }}>
      <Row depth={0} open={false} hasKids={false} active={value == null}
           name="Все" count={total} onClick={() => onChange(null)}
           onToggle={() => {}} />
      {draw('root', 0)}
      {noFolder > 0 && folders.some(f => typeof f.id === 'number') && (
        // Позиции вне дерева — заведённые у нас руками и те, что в 1С лежат
        // вне выгружаемой папки. Прятать их нельзя: по ним работают.
        <Row depth={0} open={false} hasKids={false} active={value === 'none'}
             name="Вне папок" count={noFolder}
             onClick={() => onChange('none')} onToggle={() => {}} />
      )}
    </div>
  );
}
